/**
 * DistillationLabPanel — the interactive heart.
 *
 * Pick a preset generator set; press "Distill step" to strip the next A1
 * violator (exp(X) ∉ H), or "Run to fixed point" to converge. Each generator
 * shows its gl(n,ℂ) element X, its group image exp(X) (via cmatExp), its n
 * eigenvalues (via cmatEigenvalues), the A1 membership verdict (exp(X) ∈ H ?),
 * and A2/A3/A4 as ✓ built-in over GL(n,ℂ). The attack-surface meter μ(ALLOW)
 * descends monotonically; when the A1-violation surface is empty, the verdict
 * reports that assembleCIT certifies all ten Neutralization Theorems — the
 * immune subset is structurally immune by construction.
 *
 * Honest framing: stripping a generator makes its A1-violating composite
 * unconstructable by construction — as opposed to a runtime scan/block.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useMemo, useState } from "react";
import { MathInline, tex, PanelSurface, panelTokens } from "@csk/demo-ui";
import { GENERATOR_SETS } from "../data/generatorSets.js";
import {
  attackSurface,
  distillStep,
  distillToFixedPoint,
  nextViolator,
  type Generator,
} from "../algorithms/distillation.js";
import { DistillationApplications } from "./distillation-lab-panel/DistillationApplications.js";
import { DistillationGeneratorGrid } from "./distillation-lab-panel/DistillationGeneratorGrid.js";
import { DistillationLabControls } from "./distillation-lab-panel/DistillationLabControls.js";
const SUBGROUP_LABEL: Readonly<Record<string, string>> = {
  "upper-triangular":
    "H = upper-triangular (Borel) ⊆ GL(n,ℂ) — exp(X) below-diagonal = 0",
  "special-linear": "H = SL(n,ℂ) = {g : det g = 1} ⊆ GL(n,ℂ) — exp(X) det = 1",
  diagonal: "H = diagonal torus ⊆ GL(n,ℂ) — exp(X) off-diagonals = 0",
};
export function DistillationLabPanel(): React.JSX.Element {
  const [presetId, setPresetId] = useState<string>(GENERATOR_SETS[0].id);
  const preset = useMemo(
    () => GENERATOR_SETS.find((p) => p.id === presetId) ?? GENERATOR_SETS[0],
    [presetId],
  );
  const H = preset.authoritySubgroup;
  // The live surviving set. Reset whenever the preset changes.
  const [current, setCurrent] = useState<readonly Generator[]>(
    () => preset.generators,
  );
  React.useEffect(() => {
    setCurrent(preset.generators);
  }, [preset]);
  const initialSurface = attackSurface(preset.generators);
  const surface = attackSurface(current);
  const pending = nextViolator(current, H);
  const fixedPoint = pending === null;
  // Steps remaining to immunity from the live set.
  const remainingSteps = useMemo(
    () => distillToFixedPoint(current, H).steps,
    [current, H],
  );
  const onStep = (): void => {
    setCurrent((prev) => distillStep(prev, H));
  };
  const onRun = (): void => {
    setCurrent((prev) => distillToFixedPoint(prev, H).immune);
  };
  const onReset = (): void => {
    setCurrent(preset.generators);
  };
  const verdictTone: "safe" | "neutral" = fixedPoint ? "safe" : "neutral";
  const verdictTitle = fixedPoint
    ? `Fixed point reached · μ(IMMUNE) = ${String(surface)} · assembleCIT certifies all ten Neutralization Theorems`
    : `Distilling: ${remainingSteps} step${remainingSteps === 1 ? "" : "s"} from immunity · next strips ${pending.label} (exp(X) ∉ H)`;
  const verdictBody = fixedPoint
    ? "Every surviving generator satisfies A1 (exp(X) ∈ H) — and A2, A3, A4 hold for every gl(n,ℂ) generator by construction. No A1-violating composite has a type-expressible derivation, so the immune subset is structurally immune by construction. assembleCIT yields CITOverGL: all ten NTs hold."
    : "The next distillation step removes a generator whose group image escapes H, making its A1-violating composite unconstructable by construction — as opposed to a runtime scan.";
  return (
    <PanelSurface.PanelShell
      title="Distillation Lab"
      subtitle="Pick a generator set · strip A1 violators (exp(X) ∉ H) step by step · watch μ descend to the immune fixed point"
      accentColor={panelTokens.accentGold}
    >
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          Each preset is an arbitrary{" "}
          <MathInline>{tex`\mathrm{ALLOW}_{0} \subseteq \mathfrak{gl}(n,\mathbb{C})`}</MathInline>{" "}
          of real <MathInline>{tex`\mathrm{ComplexMatrix}`}</MathInline>{" "}
          generators the operator hands VID, together with an authority subgroup{" "}
          <MathInline>{tex`H`}</MathInline>. A distillation step strips the
          first A1 violator (the generator with{" "}
          <MathInline>{tex`\exp(X) \notin H`}</MathInline>, in id order), so μ
          descends exactly one per step. A2/A3/A4 hold for every generator, so
          they never gate survival — the final fixed point is independent of
          order.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Generator set</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ChipRow
          ariaLabel="Generator set preset"
          activeId={presetId}
          onChange={setPresetId}
          items={GENERATOR_SETS.map((p) => ({
            id: p.id,
            label: p.title,
            color: panelTokens.distill,
          }))}
        />
        <p className="mx-1 mt-3 mb-0 text-[12.5px] leading-[1.6] text-[var(--vid-muted)]">
          {preset.summary}
        </p>
        <p className="mx-1 mt-2 mb-0 font-mono text-[12px] leading-[1.6] text-[var(--vid-accent)]">
          A1 authority subgroup: {SUBGROUP_LABEL[H] ?? H}
        </p>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        Attack surface μ(ALLOW)
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.MeterBar
          label="μ(ALLOW) — surviving generator count"
          value={surface}
          min={0}
          max={initialSurface}
          tone={fixedPoint ? "safe" : "info"}
          color={fixedPoint ? panelTokens.immune : panelTokens.distill}
          valueLabel={`${String(surface)} / ${String(initialSurface)}`}
          description={
            fixedPoint
              ? "Empty A1-violation surface — this is the immune fixed point."
              : `${String(remainingSteps)} distillation step${remainingSteps === 1 ? "" : "s"} from immunity.`
          }
        />
        <PanelSurface.KpiGrid>
          <PanelSurface.KpiBox
            label="μ(ALLOW₀) initial"
            value={String(initialSurface)}
            color={panelTokens.muted}
            subtitle="generators supplied"
          />
          <PanelSurface.KpiBox
            label="μ(ALLOW) now"
            value={String(surface)}
            color={fixedPoint ? panelTokens.immune : panelTokens.distill}
            subtitle="surviving generators"
          />
          <PanelSurface.KpiBox
            label="stripped so far"
            value={String(initialSurface - surface)}
            color={panelTokens.violation}
            subtitle="annihilated A1 violators"
          />
          <PanelSurface.KpiBox
            label="steps to immunity"
            value={String(remainingSteps)}
            color={fixedPoint ? panelTokens.immune : panelTokens.accentGold}
            subtitle={fixedPoint ? "fixed point" : "remaining"}
          />
        </PanelSurface.KpiGrid>
        <div className="mt-3.5">
          <PanelSurface.VerdictBanner
            tone={verdictTone}
            title={verdictTitle}
            body={verdictBody}
          />
        </div>
      </PanelSurface.Card>

      <DistillationLabControls
        fixedPoint={fixedPoint}
        onStep={onStep}
        onRun={onRun}
        onReset={onReset}
      />

      <DistillationGeneratorGrid
        generators={preset.generators}
        current={current}
        pending={pending}
        subgroup={H}
        surface={surface}
      />

      <DistillationApplications
        fixedPoint={fixedPoint}
        initialSurface={initialSurface}
        remainingSteps={remainingSteps}
        surface={surface}
      />

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "RD-65 — Vulnerability Immunity Distillation",
            path: "research/settled/RD-65-IMMUNITY-DISTILLATION.md",
          },
          {
            label: "Parametric GL(n) realization of VID",
            path: "agda/MatrixKernel/ImmunityDistillationOverMatrices.agda",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
